export const ErrorCode = {
    Error: 1000,
    MissingContentSource: 1001,
    UnknownAdProvider: 1002,
    NotAllowedError: 1003,
    NotSupportedError: 1004,
    MediaErrorAborted: 1005,
    MediaErrorDecode: 1006,
    MediaErrorNetwork: 1007,
    MediaErrorSourceNotSupported: 1008,
    PlaylistParseError: 1010,
    RequestTimeout: 1011,
    LoadError: 1012,
    UnknownMediaError: 1013,
    BufferingTimeout: 1014,
};
export const ErrorType = {
    ERROR: 'Error',
    SETUP_ERROR: 'SetupError',
    LOAD_ERROR: 'LoadError',
    PLAY_ERROR: 'PlayError',
};
export const ErrorName = {
    Error: 'Error',
    MediaErrorAborted: 'MediaErrorAborted',
    MediaErrorDecode: 'MediaErrorDecode',
    MediaErrorNetwork: 'MediaErrorNetwork',
    MediaErrorSourceNotSupported: 'MediaErrorSourceNotSupported',
    UnknownMediaError: 'UnknownMediaError',
    NotAllowedError: 'NotAllowedError',
    NotSupportedError: 'NotSupportedError',
    MissingContentSource: 'MissingContentSource',
    UnknownAdProvider: 'UnknownAdProvider',
    PlaylistParseError: 'PlaylistParseError',
    RequestTimeout: 'RequestTimeout',
    BufferingTimeout: 'BufferingTimeout',
    LoadError: 'LoadError',
    MetadataError: 'MetadataError',
};
export const MediaErrorMap = new Map(typeof MediaError !== 'undefined'
    ? [
        [MediaError.MEDIA_ERR_ABORTED, ErrorName.MediaErrorAborted],
        [MediaError.MEDIA_ERR_DECODE, ErrorName.MediaErrorDecode],
        [MediaError.MEDIA_ERR_NETWORK, ErrorName.MediaErrorNetwork],
        [
            MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED,
            ErrorName.MediaErrorSourceNotSupported,
        ],
    ]
    : []);
const mediaErrors = {
    [ErrorName.MediaErrorAborted]: {
        message: 'Media error aborted',
        code: ErrorCode.MediaErrorAborted,
        type: ErrorType.LOAD_ERROR,
        name: ErrorName.MediaErrorAborted,
    },
    [ErrorName.MediaErrorNetwork]: {
        message: 'Media error network',
        code: ErrorCode.MediaErrorNetwork,
        type: ErrorType.LOAD_ERROR,
        name: ErrorName.MediaErrorNetwork,
    },
    [ErrorName.MediaErrorDecode]: {
        message: 'Media error decode',
        code: ErrorCode.MediaErrorDecode,
        type: ErrorType.LOAD_ERROR,
        name: ErrorName.MediaErrorDecode,
    },
    [ErrorName.MediaErrorSourceNotSupported]: {
        message: 'Media error source not supported',
        code: ErrorCode.MediaErrorSourceNotSupported,
        type: ErrorType.LOAD_ERROR,
        name: ErrorName.MediaErrorSourceNotSupported,
    },
    [ErrorName.UnknownMediaError]: {
        message: 'Unknown media error',
        code: ErrorCode.UnknownMediaError,
        type: ErrorType.LOAD_ERROR,
        name: ErrorName.UnknownMediaError,
    },
};
const domExceptionErrors = {
    [ErrorName.NotAllowedError]: {
        message: 'User agent disallowed playback',
        code: ErrorCode.NotAllowedError,
        type: ErrorType.PLAY_ERROR,
        name: ErrorName.NotAllowedError,
    },
    [ErrorName.NotSupportedError]: {
        message: 'Media source format is not supported',
        code: ErrorCode.NotSupportedError,
        type: ErrorType.PLAY_ERROR,
        name: ErrorName.NotSupportedError,
    },
};
const errors = {
    [ErrorName.Error]: {
        message: 'Error',
        code: ErrorCode.Error,
        type: ErrorType.ERROR,
        name: ErrorName.Error,
    },
    ...mediaErrors,
    ...domExceptionErrors,
    [ErrorName.MissingContentSource]: {
        message: 'Missing content source',
        code: ErrorCode.MissingContentSource,
        type: ErrorType.SETUP_ERROR,
        name: ErrorName.MissingContentSource,
    },
    [ErrorName.UnknownAdProvider]: {
        message: 'Unknown ad provider',
        code: ErrorCode.UnknownAdProvider,
        type: ErrorType.SETUP_ERROR,
        name: ErrorName.UnknownAdProvider,
    },
    [ErrorName.PlaylistParseError]: {
        message: 'Failed to parse a playlist due to malformed data',
        code: ErrorCode.PlaylistParseError,
        type: ErrorType.LOAD_ERROR,
        name: ErrorName.PlaylistParseError,
    },
    [ErrorName.RequestTimeout]: {
        message: 'Request Failed',
        code: ErrorCode.RequestTimeout,
        type: ErrorType.LOAD_ERROR,
        name: ErrorName.RequestTimeout,
    },
    [ErrorName.LoadError]: {
        message: 'Failed to load any source',
        code: ErrorCode.LoadError,
        type: ErrorType.LOAD_ERROR,
        name: ErrorName.LoadError,
    },
    [ErrorName.MetadataError]: {
        message: 'Failed to load or parse metadata',
        code: ErrorCode.Error,
        type: ErrorType.ERROR,
        name: ErrorName.MetadataError,
        fatal: false,
    },
    [ErrorName.BufferingTimeout]: {
        message: 'Failed to recover from buffering state in time',
        code: ErrorCode.BufferingTimeout,
        type: ErrorType.PLAY_ERROR,
        name: ErrorName.BufferingTimeout,
    },
};
export class PlayerError {
    message;
    code;
    type;
    name;
    fatal;
    context;
    constructor(message, code, type, name, fatal, context) {
        this.message = message;
        this.code = code;
        this.type = type;
        this.name = name;
        this.fatal = fatal;
        this.context = context;
    }
    static createError(error, customMessage) {
        let name;
        let mediaErrorMessage;
        if (error instanceof PlayerError) {
            return error;
        }
        if (error instanceof MediaError) {
            name =
                MediaErrorMap.get(error?.code) ??
                    ErrorName.UnknownMediaError;
            mediaErrorMessage =
                error?.message || `Unknown MediaError. Error code: ${error?.code}`;
        }
        else {
            name =
                typeof error === 'string'
                    ? error
                    : error?.name || ErrorName.Error;
        }
        const { message: playerErrorMessage, code, type, fatal, } = PlayerError.getErrorDetails(name);
        const message = customMessage ||
            mediaErrorMessage ||
            error?.message ||
            playerErrorMessage ||
            'Unknown Error';
        const { fatal: customErrorFatal, context } = (error || {});
        return new PlayerError(message, code, type, name, fatal ?? customErrorFatal ?? true, context);
    }
    static createLoadError(error) {
        if (error instanceof PlayerError) {
            return error;
        }
        return PlayerError.createError(ErrorName.LoadError, error?.message);
    }
    static getErrorDetails(name) {
        return (errors[name] ?? {
            name,
        });
    }
}
